/**
 * Law-firm (สำนักงานทนายความ / สำนักงานกฎหมาย) Q&A cluster.
 *
 * A hand-authored core set of engagement-level answers, merged with the shared
 * deterministic legal-cluster expander so the hub covers practice area ×
 * location × searcher intent without any AI call at build or request time.
 *
 * Content rules: no fees, no outcome guarantees, no unverifiable statistics.
 */
import { LEGAL_QA_TARGET_CORE, buildLegalClusterQA, type WaveQA } from "@/data/_qa/legal-clusters";
import { LAWFIRM_ENGAGEMENT_AXIS, LAWFIRM_FORUM_AXIS, LAWFIRM_PRACTICE_AXIS } from "./axes";

export type LawfirmQA = WaveQA;

export const LAWFIRM_REVIEWED_TH = "กันยายน 2569";
export const LAWFIRM_REVIEWED_ISO = "2026-09-15";

const NO_PRICE = "เว็บไซต์ไม่แสดงค่าบริการ ขอใบเสนอราคาเฉพาะเรื่องได้ทางโทรศัพท์ LINE หรืออีเมล";
const ASOF = `ข้อมูลทบทวน ณ ${LAWFIRM_REVIEWED_TH} ระเบียบศาล ค่าธรรมเนียม และกำหนดเวลาตามกฎหมายเปลี่ยนได้ ควรยืนยันกับศาลหรือหน่วยงานที่มีเขตอำนาจก่อนดำเนินการ ${NO_PRICE}`;

/** Hand-authored engagement questions that lead the hub. */
const CORE: LawfirmQA[] = [
  {
    slug: "lawfirm-how-to-choose-law-firm",
    q: "เลือกสำนักงานกฎหมายอย่างไรให้เหมาะกับเรื่องของเรา",
    a: `ดูสามข้อเป็นหลัก หนึ่ง ทนายผู้จะรับผิดชอบเรื่องมีใบอนุญาตว่าความที่ใช้ได้อยู่จริงและตรวจสอบได้ที่สภาทนายความในพระบรมราชูปถัมภ์ สอง สำนักงานอธิบายขอบเขตงาน ขั้นตอน และเอกสารที่ต้องใช้เป็นลายลักษณ์อักษรได้ก่อนเริ่มงาน สาม เรื่องของท่านอยู่ในสายงานที่สำนักงานทำเป็นปกติ ไม่ใช่งานที่รับไว้เป็นครั้งแรก ควรระวังคำโฆษณาที่รับประกันผลคดีหรือรับประกันว่าชนะ เพราะผลของคดีเป็นดุลพินิจของศาล ไม่มีผู้ใดรับประกันได้ตามระเบียบวิชาชีพ ${ASOF}`,
    synonyms: ["เลือกสำนักงานกฎหมาย", "เลือกทนายความ", "หาสำนักงานทนายความที่ไหนดี"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-difference-lawyer-office-law-firm",
    q: "สำนักงานทนายความกับสำนักงานกฎหมายต่างกันอย่างไร",
    a: `ในทางปฏิบัติทั้งสองคำใช้เรียกผู้ให้บริการทางกฎหมายเหมือนกัน แต่งานว่าความในศาลต้องดำเนินการโดยทนายความผู้ได้รับใบอนุญาตเท่านั้น ขณะที่งานให้คำปรึกษา ร่างสัญญา จัดโครงสร้างธุรกิจ หรือประสานงานเอกสารกับหน่วยงาน อาจดำเนินการโดยทีมงานที่ปรึกษาภายใต้การควบคุมของทนายได้ สิ่งที่ควรถามคือใครเป็นทนายผู้รับผิดชอบเรื่อง ชื่ออะไร และจะลงชื่อในคำฟ้องหรือคำให้การให้ท่านหรือไม่ ${ASOF}`,
    synonyms: ["สำนักงานกฎหมายคืออะไร", "law firm ต่างจาก สำนักงานทนายความ"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-first-consult-preparation",
    q: "เข้าปรึกษาทนายครั้งแรกต้องเตรียมอะไรไปบ้าง",
    a: `เตรียมสามอย่าง หนึ่ง ลำดับเหตุการณ์ตามวันที่ตั้งแต่ต้นจนถึงปัจจุบันอย่างสั้น สอง เอกสารที่มีทั้งหมดเท่าที่หาได้ เช่น สัญญา ใบเสร็จ ข้อความสนทนา หนังสือบอกกล่าว หมายศาล หรือหนังสือจากหน่วยงาน สาม สิ่งที่ท่านต้องการให้เกิดขึ้นจริง เช่น ได้เงินคืน ยุติสัญญา หรือโอนกรรมสิทธิ์ ไม่จำเป็นต้องมีเอกสารครบก่อนเข้าพบ ทนายจะแจ้งว่าฉบับใดยังขาดและต้องขอคัดสำเนาจากหน่วยงานใด ${ASOF}`,
    synonyms: ["ปรึกษาทนายเตรียมเอกสารอะไร", "นัดพบทนายครั้งแรก"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-engagement-letter-scope",
    q: "หนังสือแต่งตั้งทนายและสัญญาจ้างว่าความควรระบุอะไร",
    a: `ควรระบุขอบเขตงานที่ตกลงกันให้ชัดว่าครอบคลุมชั้นใด เช่น ชั้นเจรจา ชั้นศาลชั้นต้น ชั้นอุทธรณ์ หรือชั้นบังคับคดี ระบุทนายผู้รับผิดชอบและผู้ติดต่อประสานงาน ระบุหน้าที่ของลูกความในการส่งเอกสารและการมาตามนัด ระบุวิธีรายงานความคืบหน้า และระบุการแยกกันระหว่างค่าบริการของสำนักงานกับค่าธรรมเนียมศาลหรือค่าธรรมเนียมราชการซึ่งเป็นอัตราของหน่วยงานและเรียกเก็บตามจริง การเขียนขอบเขตให้ชัดตั้งแต่ต้นช่วยป้องกันข้อโต้แย้งภายหลังว่างานใดรวมอยู่แล้วหรือไม่ ${ASOF}`,
    synonyms: ["สัญญาจ้างทนาย", "ใบแต่งทนาย", "ขอบเขตงานทนาย"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-no-outcome-guarantee",
    q: "สำนักงานรับประกันว่าจะชนะคดีได้หรือไม่",
    a: `ไม่ได้ และไม่ควรเชื่อผู้ที่รับประกัน ผลของคดีขึ้นกับพยานหลักฐานที่นำสืบได้ ข้อกฎหมายที่ใช้บังคับ และดุลพินิจของศาล สิ่งที่สำนักงานให้ได้คือการประเมินจุดแข็งจุดอ่อนของพยานหลักฐานตามข้อเท็จจริงที่ปรากฏ อธิบายทางเลือกที่มีพร้อมผลที่อาจเกิดในแต่ละทาง และดำเนินกระบวนการตามกำหนดเวลาอย่างครบถ้วน การรับประกันผลคดีขัดต่อมรรยาททนายความและเป็นสัญญาณเตือนที่ควรหลีกเลี่ยง ${ASOF}`,
    synonyms: ["ทนายบอกว่าชนะแน่เชื่อได้หรือไม่", "คำโฆษณาเรื่องผลคดี"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-verify-lawyer-licence",
    q: "ตรวจสอบว่าทนายมีใบอนุญาตจริงหรือไม่ทำได้อย่างไร",
    a: `ขอชื่อ นามสกุล และเลขที่ใบอนุญาตว่าความของทนายผู้รับผิดชอบเรื่อง แล้วตรวจสอบสถานะกับสภาทนายความในพระบรมราชูปถัมภ์ (lawyerscouncil.or.th) ก่อนลงนามในใบแต่งทนาย ควรตรวจด้วยว่าผู้ที่จะลงชื่อในคำฟ้องหรือคำให้การเป็นบุคคลเดียวกับที่แจ้งไว้ หากผู้ติดต่อไม่ใช่ทนาย ให้ขอทราบชื่อทนายผู้ควบคุมงาน ${ASOF}`,
    synonyms: ["เช็คใบอนุญาตทนาย", "ทนายจริงหรือปลอม", "สภาทนายความตรวจสอบ"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-change-lawyer-midway",
    q: "เปลี่ยนทนายกลางทางได้หรือไม่และต้องทำอย่างไร",
    a: `ทำได้ โดยยื่นคำร้องต่อศาลเพื่อถอนทนายเดิมและแต่งตั้งทนายใหม่ในสำนวนเดียวกัน สิ่งสำคัญคือขอรับสำเนาเอกสารในสำนวน รายการวันนัด และสถานะขั้นตอนล่าสุดจากทนายเดิมให้ครบก่อน เพื่อไม่ให้พลาดกำหนดเวลาที่กฎหมายกำหนด เช่น ระยะเวลายื่นอุทธรณ์ ควรดำเนินการก่อนวันนัดถัดไปพอสมควร เพราะการเปลี่ยนตัวใกล้วันนัดอาจกระทบการเตรียมคดี ${ASOF}`,
    synonyms: ["ถอนทนาย", "เปลี่ยนทนายกลางคดี", "ไม่พอใจทนายเดิม"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-client-confidentiality",
    q: "ข้อมูลที่เล่าให้ทนายฟังถูกเก็บเป็นความลับหรือไม่",
    a: `ข้อมูลของลูกความอยู่ภายใต้หน้าที่รักษาความลับตามมรรยาททนายความ และการเก็บรวบรวมข้อมูลส่วนบุคคลอยู่ภายใต้พระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 ในทางปฏิบัติควรขอทราบว่าผู้ใดในทีมเข้าถึงเอกสารได้ ข้อมูลถูกเก็บที่ใด และจะทำลายหรือส่งคืนเมื่อจบงานอย่างไร หากเรื่องต้องส่งเอกสารให้บุคคลภายนอก เช่น นักแปลรับรองหรือผู้เชี่ยวชาญ ควรระบุไว้ในขอบเขตงานตั้งแต่ต้น ${ASOF}`,
    synonyms: ["ทนายเก็บความลับ", "ข้อมูลลูกความ PDPA"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-conflict-of-interest-check",
    q: "ทำไมสำนักงานต้องถามชื่อคู่กรณีก่อนรับเรื่อง",
    a: `เพื่อตรวจผลประโยชน์ขัดกัน สำนักงานต้องไม่รับเรื่องที่ทำให้ต้องทำงานให้ทั้งสองฝ่ายในข้อพิพาทเดียวกัน หรือขัดกับหน้าที่ที่มีต่อลูกความรายก่อน การแจ้งชื่อคู่กรณี ชื่อบริษัทที่เกี่ยวข้อง และผู้ที่มีส่วนได้เสียตั้งแต่ต้น ช่วยให้สำนักงานตอบได้เร็วว่ารับเรื่องได้หรือไม่ ดีกว่าพบข้อขัดกันหลังเริ่มงานซึ่งจะทำให้ต้องถอนตัวและเสียเวลา ${ASOF}`,
    synonyms: ["ผลประโยชน์ขัดกันทนาย", "conflict of interest สำนักงานกฎหมาย"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-one-file-multi-practice",
    q: "เรื่องของเราเกี่ยวหลายสายงาน ต้องแยกจ้างหลายสำนวนหรือไม่",
    a: `ไม่ต้อง สำนักงานเปิดสำนวนเดียวและมีทนายผู้รับผิดชอบหลักหนึ่งท่านเป็นผู้ประสานทุกส่วน เช่น คดีมรดกที่มีที่ดินและทายาทอยู่ต่างประเทศ จะมีทั้งงานคดี งานที่ดิน และงานรับรองเอกสารระหว่างประเทศอยู่ในสำนวนเดียว ทำให้เอกสารถูกเก็บครั้งเดียว วันนัดถูกจัดให้ไม่ชนกัน และข้อมูลไม่ตกหล่นระหว่างทีม ${ASOF}`,
    synonyms: ["one stop service กฎหมาย", "จ้างทนายหลายเรื่อง"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-remote-client-abroad",
    q: "อยู่ต่างจังหวัดหรือต่างประเทศ ให้สำนักงานดำเนินการแทนได้หรือไม่",
    a: `ได้ในเกือบทุกขั้นตอน การประเมินเรื่องเบื้องต้นทำได้จากภาพเอกสารที่ส่งทาง LINE หรืออีเมล และการยื่นต่อศาลหรือหน่วยงานดำเนินการตามเขตอำนาจที่เกี่ยวข้อง ส่วนขั้นที่กฎหมายกำหนดให้ตัวความลงลายมือชื่อเองหรือมาแสดงตน ทีมงานจะแจ้งทางเลือกที่ใช้ได้ เช่น การมอบอำนาจในรูปแบบที่หน่วยงานปลายทางยอมรับ หรือการรับรองลายมือชื่อโดยทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร หากอยู่ต่างประเทศอาจต้องผ่านสถานทูตหรือสถานกงสุลตามที่หน่วยงานปลายทางกำหนด ${ASOF}`,
    synonyms: ["จ้างทนายจากต่างประเทศ", "มอบอำนาจให้ทนายดำเนินการ"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-court-fee-vs-service-fee",
    q: "ค่าธรรมเนียมศาลกับค่าบริการของสำนักงานต่างกันอย่างไร",
    a: `ค่าธรรมเนียมศาลและค่าธรรมเนียมราชการเป็นอัตราที่หน่วยงานกำหนดตามกฎหมาย เช่น ค่าขึ้นศาลตามทุนทรัพย์ ค่าส่งหมาย หรือค่าคัดถ่ายเอกสาร ส่วนค่าบริการของสำนักงานเป็นค่าตอบแทนการทำงานตามขอบเขตที่ตกลงกัน สองส่วนนี้ควรแยกกันในเอกสารตั้งแต่ต้น เว็บไซต์นี้ไม่แสดงอัตราค่าบริการเพราะขึ้นกับลักษณะงานจริง ท่านขอใบเสนอราคาเฉพาะเรื่องได้ และตรวจอัตราค่าธรรมเนียมศาลได้จากศาลที่มีเขตอำนาจ ${ASOF}`,
    synonyms: ["ค่าขึ้นศาล", "ค่าธรรมเนียมศาลใครจ่าย", "ค่าทนายคิดอย่างไร"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-progress-reporting",
    q: "ระหว่างดำเนินเรื่องจะทราบความคืบหน้าอย่างไร",
    a: `ควรตกลงรูปแบบการรายงานไว้ตั้งแต่ต้น เช่น สรุปสถานะเป็นข้อความหลังทุกวันนัดหรือทุกครั้งที่มีคำสั่งศาล พร้อมส่งสำเนาเอกสารที่ยื่นและที่ได้รับ วิธีที่ช่วยได้จริงคือขอ "ตารางวันนัดและกำหนดเวลา" ของเรื่อง เพื่อให้ท่านเห็นล่วงหน้าว่าต้องเตรียมพยานหรือเอกสารใดเมื่อไร หากเรื่องอยู่ระหว่างรอคิวพิจารณาของหน่วยงาน ระยะเวลาอาจนิ่งไปช่วงหนึ่งได้ตามปกติ ${ASOF}`,
    synonyms: ["ติดตามคดี", "ทนายไม่รายงานความคืบหน้า"],
    topicSlug: "engagement",
    topicTh: "การจ้างทนายและการทำงานร่วมกัน",
  },
  {
    slug: "lawfirm-limitation-period-risk",
    q: "อายุความสำคัญอย่างไรและควรทำอะไรก่อน",
    a: `อายุความเป็นกำหนดเวลาที่กฎหมายให้ใช้สิทธิเรียกร้อง หากปล่อยให้ล่วงพ้นไป ฝ่ายตรงข้ามยกขึ้นต่อสู้ได้และอาจทำให้เสียสิทธิ แม้ข้อเท็จจริงจะอยู่ข้างท่าน อายุความของแต่ละเรื่องไม่เท่ากันและเริ่มนับจากเหตุการณ์ต่างกัน จึงควรให้ทนายตรวจวันเริ่มนับจากเอกสารจริงโดยเร็วที่สุด สิ่งที่ทำได้ทันทีคือรวบรวมหลักฐานวันที่เกิดเหตุ วันที่ทราบเหตุ และหนังสือทวงถามหรือการชำระหนี้บางส่วนที่อาจกระทบการนับ ${ASOF}`,
    synonyms: ["อายุความฟ้องคดี", "หมดอายุความแล้วฟ้องได้ไหม"],
    topicSlug: "deadlines",
    topicTh: "กำหนดเวลาและอายุความ",
  },
  {
    slug: "lawfirm-received-court-summons",
    q: "ได้รับหมายศาลควรทำอะไรก่อนเป็นลำดับแรก",
    a: `อ่านหน้าหมายเพื่อดูสามข้อ คือ ศาลใด เลขคดีอะไร และวันนัดหรือกำหนดยื่นคำให้การเมื่อใด แล้วถ่ายภาพหมายทุกหน้ารวมทั้งซองและวันที่ปิดหมายส่งให้ทนายทันที เพราะกำหนดยื่นคำให้การนับจากวันที่ได้รับหมายตามวิธีที่กฎหมายกำหนด การไม่ยื่นคำให้การภายในกำหนดอาจถูกถือว่าขาดนัดและเสียโอกาสต่อสู้ในประเด็นสำคัญ ไม่ควรติดต่อคู่กรณีเพื่อเจรจาโดยยังไม่หารือทนาย เพราะข้อความที่ส่งอาจถูกใช้เป็นพยานหลักฐาน ${ASOF}`,
    synonyms: ["ได้หมายศาลทำอย่างไร", "ถูกฟ้องคดี", "หมายเรียกจำเลย"],
    topicSlug: "deadlines",
    topicTh: "กำหนดเวลาและอายุความ",
  },
  {
    slug: "lawfirm-evidence-preservation",
    q: "ควรเก็บรักษาพยานหลักฐานอย่างไรให้ใช้ได้ในชั้นศาล",
    a: `เก็บต้นฉบับไว้ให้ครบและอย่าแก้ไขเพิ่มเติมภายหลัง สำหรับหลักฐานอิเล็กทรอนิกส์ เช่น ข้อความสนทนา อีเมล หรือรายการโอนเงิน ให้บันทึกทั้งเนื้อหาและข้อมูลประกอบ เช่น วันเวลา ชื่อบัญชี และหมายเลขอ้างอิง โดยเก็บไฟล์ต้นทางไว้ด้วย ไม่ใช่เฉพาะภาพหน้าจอที่ตัดมาบางส่วน หากอุปกรณ์อาจเสียหายควรสำรองข้อมูลไว้อีกชุด ความครบถ้วนและความต่อเนื่องของหลักฐานมีผลต่อการรับฟังในชั้นพิจารณา ${ASOF}`,
    synonyms: ["เก็บหลักฐานคดี", "แชทใช้เป็นหลักฐานได้ไหม"],
    topicSlug: "deadlines",
    topicTh: "กำหนดเวลาและอายุความ",
  },
  {
    slug: "lawfirm-mediation-vs-litigation",
    q: "ควรเลือกไกล่เกลี่ยหรือฟ้องคดีดีกว่า",
    a: `พิจารณาจากสามเรื่อง หนึ่ง ความชัดของพยานหลักฐาน หากเอกสารครบและข้อเท็จจริงไม่ซับซ้อน การฟ้องอาจได้ผลบังคับที่ชัดเจนกว่า สอง ความสามารถชำระของฝ่ายตรงข้าม เพราะคำพิพากษาที่บังคับไม่ได้ก็ไม่แก้ปัญหา สาม ความสัมพันธ์ที่ต้องรักษาต่อ เช่น คู่ค้าหรือครอบครัว การไกล่เกลี่ยมักใช้เวลาน้อยกว่าและควบคุมข้อตกลงได้เอง แต่ต้องทำข้อตกลงให้บังคับได้จริง ในหลายกรณีใช้สองทางประกอบกันได้ คือเจรจาโดยเตรียมสำนวนไว้พร้อมยื่นหากเจรจาไม่สำเร็จ ${ASOF}`,
    synonyms: ["ไกล่เกลี่ยหรือฟ้อง", "เจรจากับฟ้องคดีเลือกอะไร"],
    topicSlug: "deadlines",
    topicTh: "กำหนดเวลาและอายุความ",
  },
  {
    slug: "lawfirm-foreigner-client-language",
    q: "ชาวต่างชาติใช้บริการได้หรือไม่และเอกสารต้องเป็นภาษาไทยทั้งหมดหรือเปล่า",
    a: `ใช้บริการได้ การให้คำปรึกษาและการติดต่อทำเป็นภาษาอังกฤษได้ แต่คำฟ้อง คำให้การ และเอกสารที่ยื่นต่อศาลหรือหน่วยงานราชการไทยต้องจัดทำเป็นภาษาไทยตามที่หน่วยงานกำหนด เอกสารต่างประเทศที่จะใช้อ้างอิงมักต้องมีคำแปลรับรอง และในบางกรณีต้องผ่านการรับรองนิติกรณ์หรือการรับรองจากสถานทูตหรือสถานกงสุลก่อน ทีมงานจะระบุให้ชัดว่าเอกสารฉบับใดต้องแปลและต้องรับรองด้วยวิธีใด ${ASOF}`,
    synonyms: ["foreigner lawyer thailand", "ชาวต่างชาติจ้างทนายไทย", "เอกสารภาษาอังกฤษยื่นศาลไทย"],
    topicSlug: "cross-border",
    topicTh: "ลูกความต่างชาติและเรื่องข้ามประเทศ",
  },
  {
    slug: "lawfirm-thai-abroad-power-of-attorney",
    q: "คนไทยที่อยู่ต่างประเทศจะมอบอำนาจให้ดำเนินการในไทยอย่างไร",
    a: `โดยทั่วไปทำหนังสือมอบอำนาจตามแบบที่หน่วยงานปลายทางกำหนด แล้วให้สถานเอกอัครราชทูตหรือสถานกงสุลไทยรับรองลายมือชื่อ หรือดำเนินการตามวิธีที่หน่วยงานนั้นยอมรับ ก่อนจัดทำควรยืนยันกับหน่วยงานปลายทางก่อนว่าใช้แบบใด ต้องระบุอำนาจเฉพาะเรื่องหรืออำนาจทั่วไป และต้องแนบเอกสารประกอบใดของผู้มอบอำนาจ เพราะแบบที่ผิดหรือถ้อยคำที่กว้างเกินไปเป็นสาเหตุที่ถูกตีกลับบ่อยที่สุด ${ASOF}`,
    synonyms: ["หนังสือมอบอำนาจจากต่างประเทศ", "คนไทยในต่างประเทศมอบอำนาจ"],
    topicSlug: "cross-border",
    topicTh: "ลูกความต่างชาติและเรื่องข้ามประเทศ",
  },
  {
    slug: "lawfirm-company-vs-individual-instruction",
    q: "ถ้าผู้ว่าจ้างเป็นนิติบุคคล ใครลงนามแต่งตั้งทนายได้",
    a: `ผู้มีอำนาจลงนามผูกพันนิติบุคคลตามหนังสือรับรองฉบับล่าสุดเป็นผู้ลงนาม และต้องลงนามให้ครบตามเงื่อนไข เช่น กรรมการสองคนลงนามร่วมกันและประทับตราสำคัญหากข้อบังคับกำหนด ควรแนบหนังสือรับรองที่ออกไม่เกินระยะเวลาที่ผู้รับเอกสารกำหนด พร้อมสำเนาบัตรประจำตัวของผู้ลงนาม หากต้องการให้พนักงานเป็นผู้ประสานงาน ให้ทำหนังสือมอบอำนาจแยกต่างหากระบุขอบเขตให้ชัด ${ASOF}`,
    synonyms: ["บริษัทแต่งตั้งทนาย", "กรรมการลงนามใบแต่งทนาย"],
    topicSlug: "cross-border",
    topicTh: "ลูกความต่างชาติและเรื่องข้ามประเทศ",
  },
  {
    slug: "lawfirm-notary-role-in-law-firm",
    q: "งานรับรองเอกสารโดยทนาย (Notary) เกี่ยวข้องกับงานสำนักงานกฎหมายอย่างไร",
    a: `เป็นส่วนหนึ่งของงานเอกสารระหว่างประเทศที่มักเกิดขึ้นระหว่างเรื่องหลัก เช่น การมอบอำนาจให้ดำเนินคดี การรับรองลายมือชื่อผู้ถือหุ้นต่างประเทศ หรือการรับรองสำเนาเอกสารเพื่อยื่นต่อหน่วยงานต่างประเทศ ในไทยดำเนินการโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารที่ขึ้นทะเบียนกับสภาทนายความ ผลของการรับรองคือรับรองข้อเท็จจริงที่ปรากฏต่อหน้าผู้รับรอง ไม่ใช่รับรองว่าเนื้อหาของเอกสารถูกต้องตามกฎหมายปลายทาง จึงควรตรวจข้อกำหนดของผู้รับเอกสารก่อนทุกครั้ง ${ASOF}`,
    synonyms: ["notary public ไทยคืออะไร", "ทนายรับรองเอกสาร"],
    topicSlug: "cross-border",
    topicTh: "ลูกความต่างชาติและเรื่องข้ามประเทศ",
  },
  {
    slug: "lawfirm-accounting-with-legal",
    q: "ใช้บริการกฎหมายและบัญชีจากที่เดียวกันมีข้อดีอย่างไร",
    a: `ข้อดีคือข้อมูลชุดเดียวถูกใช้ได้ทั้งสองงาน เช่น การจดทะเบียนเปลี่ยนแปลงกรรมการหรือทุนจะสอดคล้องกับงบการเงินและแบบภาษีที่ยื่น หรือกรณีข้อพิพาททางภาษี เอกสารทางบัญชีที่จัดเก็บครบตั้งแต่ต้นช่วยให้เตรียมคำชี้แจงได้เร็ว สิ่งที่ควรตกลงให้ชัดคือใครรับผิดชอบส่งเอกสารรอบใด และใครเป็นผู้อนุมัติรายการก่อนยื่น เพราะการจ้างผู้ทำบัญชีไม่ทำให้หน้าที่ตามกฎหมายของนิติบุคคลและกรรมการสิ้นสุดลง ${ASOF}`,
    synonyms: ["สำนักงานบัญชีและกฎหมายที่เดียว", "จ้างบัญชีพร้อมทนาย"],
    topicSlug: "cross-border",
    topicTh: "ลูกความต่างชาติและเรื่องข้ามประเทศ",
  },
  {
    slug: "lawfirm-what-if-cannot-take-case",
    q: "ถ้าสำนักงานรับเรื่องของเราไม่ได้ จะเกิดอะไรขึ้น",
    a: `สำนักงานจะแจ้งเหตุผลตามข้อเท็จจริง เช่น มีผลประโยชน์ขัดกัน เรื่องอยู่นอกสายงานที่ทำเป็นปกติ หรือกำหนดเวลาที่เหลือไม่พอจะทำงานให้ได้อย่างมีคุณภาพ พร้อมคืนเอกสารที่ท่านส่งมาและไม่เก็บข้อมูลไว้เกินความจำเป็น การปฏิเสธอย่างตรงไปตรงมาดีกว่ารับไว้แล้วทำไม่ทัน เพราะเรื่องที่มีกำหนดเวลาตามกฎหมายเสียหายได้ทันทีเมื่อพลาดกำหนด ${ASOF}`,
    synonyms: ["ทนายไม่รับคดี", "สำนักงานปฏิเสธรับเรื่อง"],
    topicSlug: "cross-border",
    topicTh: "ลูกความต่างชาติและเรื่องข้ามประเทศ",
  },
];

const generated = buildLegalClusterQA({
  prefix: "lawfirm",
  clusterTh: "งานของสำนักงานทนายความและสำนักงานกฎหมาย",
  authorityTh: "ศาลหรือหน่วยงานที่มีเขตอำนาจในเรื่องนั้น",
  reviewedTh: LAWFIRM_REVIEWED_TH,
  volatileNote:
    "ระเบียบศาล ค่าธรรมเนียม และกำหนดเวลาตามกฎหมายเปลี่ยนได้ ควรยืนยันกับศาลหรือหน่วยงานที่มีเขตอำนาจก่อนดำเนินการ",
  topics: LAWFIRM_PRACTICE_AXIS,
  extraTopics: [...LAWFIRM_ENGAGEMENT_AXIS, ...LAWFIRM_FORUM_AXIS],
  qaTarget: LEGAL_QA_TARGET_CORE,
});

const seen = new Set(CORE.map((q) => q.slug));

export const LAWFIRM_QA: LawfirmQA[] = [...CORE, ...generated.filter((q) => !seen.has(q.slug))];

export const LAWFIRM_QA_BY_SLUG: Record<string, LawfirmQA> = Object.fromEntries(
  LAWFIRM_QA.map((qa) => [qa.slug, qa]),
);

export const LAWFIRM_QA_COUNT = LAWFIRM_QA.length;
